import { Link } from "react-router";

import { BarChart3Icon } from "lucide-react";

import { DetailSection } from "@/components/show";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DetailNotice } from "@/pages/sports/components/detail-notice";
import { AfterMatchNote } from "@/pages/sports/components/read-only-note";
import { SectionBoundary } from "@/pages/sports/components/section-boundary";
import { useSportsDetail } from "@/pages/sports/hooks/use-sports-detail";
import { fmtCount } from "@/pages/sports/lib/format";
import { teamSeasonStatsViewSchema } from "@/pages/teams/schemas/team-season-stats.view";
import type {
  ITeamCompetitionStats,
  ITeamSplit,
} from "@/types/team-season-stats";

const SPLITS = [
  { key: "total", label: "Total" },
  { key: "home", label: "Home" },
  { key: "away", label: "Away" },
] as const;

const num = "text-right tabular-nums";
const groupStart = "border-l";

function CompetitionName({ row }: { row: ITeamCompetitionStats }) {
  const name = row.league?.name ?? row.leagueName;
  if (!name) return <span className="text-muted-foreground">—</span>;
  return row.league?.id ? (
    <Link to={`/leagues/show/${row.league.id}`} className="hover:underline">
      {name}
    </Link>
  ) : (
    <>{name}</>
  );
}

function SplitCells({
  split,
  showDrawn,
}: {
  split?: ITeamSplit | null;
  showDrawn: boolean;
}) {
  return (
    <>
      <TableCell className={`${num} ${groupStart}`}>
        {fmtCount(split?.played)}
      </TableCell>
      <TableCell className={num}>{fmtCount(split?.won)}</TableCell>
      {showDrawn && (
        <TableCell className={num}>{fmtCount(split?.drawn)}</TableCell>
      )}
      <TableCell className={num}>{fmtCount(split?.lost)}</TableCell>
      <TableCell className={num}>{fmtCount(split?.goalsFor)}</TableCell>
      <TableCell className={num}>{fmtCount(split?.goalsAgainst)}</TableCell>
    </>
  );
}

/**
 * The team's current-season record per competition (total, home, away), read from
 * `GET /api/teams/{id}/season-stats`. For / Against count goals in football and
 * points in basketball and volleyball, so they are never labelled "Goals". The draws
 * column is left out for a sport without draws. A wrongly typed value in the payload falls
 * back to one muted line instead of blanking the page.
 */
export function TeamSeasonStatsSection({ teamId }: { teamId: string }) {
  return (
    <SectionBoundary key={teamId} what="season stats">
      <TeamSeasonStats teamId={teamId} />
    </SectionBoundary>
  );
}

function TeamSeasonStats({ teamId }: { teamId: string }) {
  const state = useSportsDetail(
    `teams/${teamId}/season-stats`,
    teamSeasonStatsViewSchema,
    "team season stats",
  );
  if (state.status !== "ready") {
    return (
      <DetailNotice
        state={state}
        what="season stats"
        emptyText="No season stats yet"
      />
    );
  }

  const competitions = state.data.competitions ?? [];
  if (!competitions.length) {
    return (
      <DetailNotice
        state={{ status: "empty" }}
        what="season stats"
        emptyText="No season stats yet"
      />
    );
  }

  const showDrawn = competitions.some((c) =>
    SPLITS.some((s) => c[s.key]?.drawn != null),
  );
  const perSplit = showDrawn ? 6 : 5;
  const season = state.data.season;

  return (
    <DetailSection
      title={season ? `Season stats · ${season}` : "Season stats"}
      icon={<BarChart3Icon className="size-4" />}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead rowSpan={2}>Competition</TableHead>
            {SPLITS.map((s) => (
              <TableHead
                key={s.key}
                colSpan={perSplit}
                className={`${groupStart} text-center`}
              >
                {s.label}
              </TableHead>
            ))}
          </TableRow>
          <TableRow>
            {SPLITS.map((s) => (
              <SplitHeads key={s.key} showDrawn={showDrawn} />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {competitions.map((row, i) => (
            <TableRow key={row.league?.id ?? row.leagueName ?? i}>
              <TableCell className="font-medium">
                <CompetitionName row={row} />
              </TableCell>
              {SPLITS.map((s) => (
                <SplitCells
                  key={s.key}
                  split={row[s.key]}
                  showDrawn={showDrawn}
                />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="mt-3 text-xs text-muted-foreground">
        For / Against count goals in football and points in basketball and
        volleyball.
      </p>
      <div className="mt-2">
        <AfterMatchNote />
      </div>
    </DetailSection>
  );
}

function SplitHeads({ showDrawn }: { showDrawn: boolean }) {
  return (
    <>
      <TableHead className={`${num} ${groupStart}`} title="Played">
        P
      </TableHead>
      <TableHead className={num} title="Won">
        W
      </TableHead>
      {showDrawn && (
        <TableHead className={num} title="Drawn">
          D
        </TableHead>
      )}
      <TableHead className={num} title="Lost">
        L
      </TableHead>
      <TableHead className={num}>For</TableHead>
      <TableHead className={num}>Against</TableHead>
    </>
  );
}
